<!-- HEADER -->
<div class="mb-6 flex items-center justify-between gap-4">
	<div class="text-base font-bold text-black">
		{{ pilot?.name }} [<span class="text-violet">{{ pilot?.staffNumber }}</span
		>]
	</div>
	<div class="flex cursor-pointer items-center gap-3 font-medium text-grey-40">
		<span class="whitespace-nowrap text-red">Pilot Data Management</span>
		<span>></span>
		<span class="text-red" (click)="goBack()">Folders</span>
		<span>></span>
		<span class="line-clamp-1">{{ folderDisplayName }}</span>
	</div>
</div>

<!-- UPLOAD FILE -->
<form [formGroup]="uploadForm">
	<div class="mb-3 flex items-end justify-between gap-4">
		<div class="form-element relative w-full flex-1 gap-2">
			<mat-label for="form-file">Select File</mat-label>
			<mat-form-field appearance="outline" class="form-field form-field-responsive mt-1 w-full">
				@if (selectedFiles.length === 0) {
					<textarea
						matInput
						formControlName="files"
						class="w-full resize-none !px-2"
						placeholder="Select File"
						[value]="selectedFilesDisplay"
						readonly
						(click)="fileInput.click()"
						[rows]="1"
					></textarea>
				} @else {
					<input
						matInput
						type="text"
						[value]="selectedFiles.length + ' file(s) selected'"
						readonly
						style="display: none"
					/>
				}

				@if (selectedFiles.length === 0) {
					<img class="absolute right-1 top-1/2 w-4 -translate-y-1/2" src="/assets/icon_search_2.svg" alt="Search" />
				}

				@if (selectedFiles.length > 0) {
					<div class="min-h-[40px] w-full p-2">
						@for (file of selectedFiles; track $index) {
							<div
								class="mb-1 flex items-center justify-between rounded border bg-gray-50 px-2 py-1 transition-colors hover:bg-gray-100"
							>
								<span class="flex-1 truncate text-sm">{{ file.name }}</span>
								<button
									type="button"
									class="ml-2 text-red-500 hover:text-red-700 focus:outline-none"
									(click)="removeFile($index)"
									title="Remove file"
								>
									<span class="aa-mat-icon text-sm">close</span>
								</button>
							</div>
						}
						<button
							type="button"
							class="flex w-full items-center justify-center rounded border-2 border-dashed border-gray-300 py-1 text-sm text-blue-600 transition-colors hover:border-blue-400 hover:text-blue-800 focus:outline-none"
							(click)="fileInput.click()"
						>
							<span class="aa-mat-icon mr-1 text-sm">add</span>
							Add More Files
						</button>
					</div>
				}

				@if (formFiles.errors) {
					<mat-error>
						{{ sharedService.getErrorMessage(formFiles) }}
					</mat-error>
				}
			</mat-form-field>
			<input #fileInput type="file" hidden (change)="onFileSelected($event)" [accept]="allowedFileTypes" multiple />
		</div>

		<button (click)="uploadNewFile()" class="aa-btn shrink-0" data-variant="primary" data-size="small">Upload</button>
	</div>
</form>

<div class="mb-4 flex justify-end">
	<app-cbta-daterange-filter
		[startDate]="startDate"
		[endDate]="endDate"
		(dateRangeSelected)="handleDateRangeChange($event)"
	></app-cbta-daterange-filter>
</div>

<!-- FILES TABLE -->
<div class="cbta-tbl files-tbl">
	<div class="columns">
		<div class="column col-12">
			@if (hasFiles) {
				<ngx-table
					[configuration]="configuration"
					[data]="files"
					[noResultsTemplate]="noResultsTemplate"
					[columns]="columns"
					[pagination]="pagination"
					(event)="onTableChanged($event)"
				>
				</ngx-table>
			} @else {
				<div class="flex h-[300px] items-center justify-center rounded border bg-[#FBFCFE] text-grey-40">
					<div class="text-center">
						<span class="aa-mat-icon text-grey-30 mb-4 text-6xl">folder_open</span>
						<div class="text-lg font-medium">No Files Uploaded</div>
						<div class="mt-2 text-sm">Upload your first file to get started</div>
					</div>
				</div>
			}
		</div>

		<!-- TEMPLATE: File Name -->
		<ng-template #fileNameTpl let-row let-rowIndex="rowIndex">
			<div class="flex items-center gap-3">
				<span class="truncate font-medium">{{ extractFileName(row.name) }}</span>
			</div>
		</ng-template>

		<!-- TEMPLATE: Uploaded By -->
		<ng-template #uploadedByTpl let-row let-rowIndex="rowIndex">
			<div class="flex items-center gap-2">
				<span>{{ row.lastUploadedBy }}</span>
			</div>
		</ng-template>

		<!-- TEMPLATE: Uploaded On -->
		<ng-template #uploadedOnTpl let-row let-rowIndex="rowIndex">
			<span class="font-medium">
				{{ formatDate(row.lastUploadedDate) }}
			</span>
		</ng-template>

		<!-- TEMPLATE: Action Buttons -->
		<ng-template #actionTpl let-row let-rowIndex="rowIndex">
			<div class="flex items-center justify-center gap-2">
				<button
					class="flex size-8 items-center justify-center rounded-[3px] bg-red text-white hover:bg-red/90"
					(click)="viewFileDetails(row)"
					title="View Details"
				>
					<span class="aa-mat-icon text-sm">visibility</span>
				</button>

				<button
					class="flex size-8 items-center justify-center rounded-[3px] bg-red text-white hover:bg-red/90"
					(click)="deleteFile(row)"
					title="Delete"
				>
					<span class="aa-mat-icon text-sm">delete</span>
				</button>
			</div>
		</ng-template>

		<!-- TEMPLATE: No Results -->
		<ng-template #noResultsTemplate>
			<td [attr.colspan]="columns.length" class="h-[180px] text-center">
				<div class="text-center text-grey-40">
					<span class="aa-mat-icon mb-2 text-4xl">search_off</span>
					<div>No files match your search criteria.</div>
				</div>
			</td>
		</ng-template>
	</div>
</div>

<!-- NAVIGATION -->
<div class="mt-24 flex items-center justify-between gap-4 border-t border-blue-20 pt-4">
	<button (click)="goBack()" class="aa-btn" data-variant="tertiary">Back</button>
</div>
